/**
 * Signaling over Nostr relays, the first strategy tried.
 *
 * Nostr relays are plentiful, run by many separate people, and speak a plain WebSocket protocol
 * that carries arbitrary encrypted events. That makes them the most likely to be reachable from an
 * arbitrary network, which is why they go first.
 *
 * The library is imported dynamically so a session that never needs the fallback never downloads
 * it, and so the whole of Trystero stays out of the first chunk a player loads.
 */

import { probeRelays } from '@/net/signaling/relaySocketProbe';
import type * as Trystero from 'trystero';
import type { SignalingStrategy } from '@/net/signaling/signalingContract';

const load = (): Promise<typeof Trystero> => import('trystero');

const relays = async (): Promise<readonly string[]> => (await load()).defaultRelayUrls;

export const NOSTR_SIGNALING = {
  id: 'nostr',
  label: 'Nostr relays',
  relays,
  selfId: async (): Promise<string> => (await load()).selfId,
  isReachable: async (timeoutMs, signal): Promise<boolean> =>
    probeRelays(await relays(), timeoutMs, signal),
  join: async (config, roomId, callbacks) => (await load()).joinRoom(config, roomId, callbacks),
} as const satisfies SignalingStrategy;
